import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ALLOWED_CONTENT_TYPES,
  createInvoice,
  INVOICE_CATEGORIES,
  MAX_IMAGE_SIZE_BYTES,
  uploadToS3,
  type InvoiceCategory
} from '../lib/invoices';

export function InvoiceNewPage() {
  const navigate = useNavigate();
  const [category, setCategory] = useState<InvoiceCategory>('Petrol');
  const [otherReason, setOtherReason] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function pickFile(e: React.ChangeEvent<HTMLInputElement>) {
    setError(null);
    const f = e.target.files?.[0] ?? null;
    if (f) {
      if (f.size > MAX_IMAGE_SIZE_BYTES) {
        setError(`Image is too large (max 10 MB).`);
        setFile(null);
        return;
      }
      if (!ALLOWED_CONTENT_TYPES.includes(f.type)) {
        setError(`Unsupported image type ${f.type || '(unknown)'} . Use JPEG, PNG, HEIC, or WebP.`);
        setFile(null);
        return;
      }
    }
    setFile(f);
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (!file) {
      setError('Please choose or take a photo of the invoice.');
      return;
    }
    if (category === 'Other' && otherReason.trim().length === 0) {
      setError('Please provide a short reason for "Other".');
      return;
    }
    setSubmitting(true);
    try {
      const created = await createInvoice({
        category,
        otherReason: category === 'Other' ? otherReason.trim() : undefined,
        fileName: file.name,
        contentType: file.type
      });
      await uploadToS3(created.uploadUrl, file);
      navigate(`/invoices/${created.invoiceId}`, { replace: true });
    } catch (e2) {
      setError(String((e2 as Error)?.message ?? e2));
      setSubmitting(false);
    }
  }

  return (
    <div style={{ maxWidth: 520 }}>
      <h1>New invoice</h1>
      <form onSubmit={submit} className="section-card" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <label style={label}>
          Category
          <select
            value={category}
            onChange={(e) => setCategory(e.target.value as InvoiceCategory)}
            style={input}
            required
          >
            {INVOICE_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </label>

        {category === 'Other' && (
          <label style={label}>
            Reason
            <input
              type="text"
              value={otherReason}
              onChange={(e) => setOtherReason(e.target.value)}
              placeholder="e.g. office supplies"
              maxLength={200}
              required
              style={input}
            />
          </label>
        )}

        <label style={label}>
          Receipt photo (max 10 MB)
          {/* No `capture` attribute on purpose. With `capture="environment"`,
              iOS Safari skips the chooser sheet entirely and opens the rear
              camera, which means users can't pick an existing receipt photo
              from their library. Leaving it off makes mobile browsers show
              the normal chooser (Photo Library / Take Photo / Choose File on
              iOS, Camera / Photos / Files on Android), so the camera path is
              still one tap away but isn't forced. `accept` still restricts
              the chooser to image types. */}
          <input
            type="file"
            accept="image/jpeg,image/png,image/heic,image/webp"
            onChange={pickFile}
            required
            style={input}
          />
        </label>

        {error && <p style={errStyle}>{error}</p>}

        <div style={{ display: 'flex', gap: 12 }}>
          <button type="submit" disabled={submitting} style={btnPrimary}>
            {submitting ? 'Uploading...' : 'Submit invoice'}
          </button>
          <button type="button" onClick={() => navigate(-1)} disabled={submitting} style={btn}>
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
}

const label: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 6,
  fontSize: 14,
  fontWeight: 600,
  color: 'var(--sj-text-dark)'
};

const input: React.CSSProperties = {
  padding: '10px 12px',
  fontSize: 14,
  border: '1px solid var(--sj-line-strong)',
  borderRadius: 'var(--radius-xs)'
};

const btn: React.CSSProperties = {
  padding: '10px 18px',
  fontSize: 14,
  fontWeight: 600,
  background: 'var(--sj-paper)',
  color: 'var(--sj-text-dark)',
  border: '1px solid var(--sj-line-strong)',
  borderRadius: 999,
  cursor: 'pointer'
};

const btnPrimary: React.CSSProperties = {
  ...btn,
  background: 'var(--sj-crimson)',
  color: '#fff',
  borderColor: 'var(--sj-crimson)',
  letterSpacing: '0.04em',
  textTransform: 'uppercase'
};

const errStyle: React.CSSProperties = {
  background: 'var(--sj-danger-bg)',
  border: '1px solid var(--sj-danger-line)',
  color: 'var(--sj-danger-fg)',
  padding: 12,
  borderRadius: 'var(--radius-xs)'
};
